import { AxiosPromise } from 'axios';
import { httpService } from 'core-utilities';
import {
  AccountPinError,
  AccountSecurityError,
  ChangePasswordReturnType,
  CHANGE_PASSWORD_CONFIG,
  EmailError,
  GetAccountPinStateReturnType,
  GetSecurityPromptParametersReturnType,
  GET_ACCOUNT_PIN_STATE_CONFIG,
  GET_SECURITY_PROMPT_PARAMETERS_CONFIG,
  LockAccountPinReturnType,
  LOCK_ACCOUNT_PIN_CONFIG,
  PasswordError,
  PasswordResetError,
  RESET_SEND_PROMPTED_CONFIG,
  UnlockAccountPinReturnType,
  UNLOCK_ACCOUNT_PIN_CONFIG,
  UPDATE_EMAIL_CONFIG
} from '../constants/requests';
import { Result } from '../constants/types';

/**
 * Parses a JavaScript object, which can take on any type, into an array of
 * error codes based on the typical schema returned by our back-end.
 *
 * TODO: Move this functionality into a shared library.
 */
const getApiErrorCodes = (error: unknown): number[] => {
  const errorCodes: number[] = [];
  if (!error || typeof error !== 'object') {
    return [];
  }

  const { errors } = error as Record<string, unknown>;
  if (!(errors instanceof Array)) {
    return [];
  }

  errors.forEach((errorObject: unknown) => {
    if (!errorObject || typeof errorObject !== 'object') {
      return;
    }

    const { code } = errorObject as Record<string, unknown>;
    if (typeof code === 'number') {
      errorCodes.push(code);
    }
  });

  return errorCodes;
};

/**
 * Gets a single error code based on a JS object thrown by Axios.
 *
 * TODO: Move this functionality into a shared library.
 */
const parseErrorCode = (error: unknown): number | null => {
  const errorCodes = getApiErrorCodes(error);
  if (typeof error === 'object') {
    // Sometimes the response returned by Axios hides the errors in `error.data`.
    getApiErrorCodes((error as Record<string, unknown>).data).forEach(item =>
      errorCodes.push(item)
    );
  }

  return errorCodes[0] || null;
};

/**
 * Converts an unknown value into an enum type.
 *
 * TODO: Move this functionality into a shared library.
 */
const toEnum = <T>(enumType: T, value: unknown): T[keyof T] | null => {
  if (Object.values(enumType).includes(value)) {
    return value as T[keyof T];
  }

  return null;
};

/**
 * Converts an Axios HTTP request into a `Result`.
 *
 * TODO: Move this functionality into a shared library.
 */
const toResult = async <T, M>(
  request: AxiosPromise<T>,
  errorEnum: M
): Promise<Result<T, M[keyof M]>> => {
  try {
    const response = await request;
    return Result.ok(response.data);
  } catch (error) {
    const errorCode = parseErrorCode(error);
    return Result.error(toEnum(errorEnum, errorCode));
  }
};

/**
 * A class encapsulating the various HTTP requests in this web app.
 *
 * Note that all of the requests return `Result` types, which can be explicitly
 * instantiated with either a response value or an error, depending on whether
 * the request succeeded. The requests should only throw an error if a fatal
 * (i.e. unexpected) exception occurred.
 */
export class RequestServiceDefault {
  changePassword = (
    currentPassword: string,
    newPassword: string
  ): Promise<Result<ChangePasswordReturnType, PasswordError>> =>
    toResult(
      httpService.post(CHANGE_PASSWORD_CONFIG, {
        currentPassword,
        newPassword
      }),
      PasswordError
    );

  resetSendPrompted = (): Promise<Result<void, PasswordResetError>> =>
    toResult(httpService.post(RESET_SEND_PROMPTED_CONFIG), PasswordResetError);

  getAccountPinState = (): Promise<Result<GetAccountPinStateReturnType, AccountPinError>> =>
    toResult(httpService.get(GET_ACCOUNT_PIN_STATE_CONFIG), AccountPinError);

  unlockAccountPin = (pin: string): Promise<Result<UnlockAccountPinReturnType, AccountPinError>> =>
    toResult(httpService.post(UNLOCK_ACCOUNT_PIN_CONFIG, { pin }), AccountPinError);

  lockAccountPin = (): Promise<Result<LockAccountPinReturnType, AccountPinError>> =>
    toResult(httpService.post(LOCK_ACCOUNT_PIN_CONFIG), AccountPinError);

  updateEmail = (emailAddress: string): Promise<Result<void, EmailError>> =>
    toResult(
      httpService.post(UPDATE_EMAIL_CONFIG, {
        emailAddress,
        skipVerificationEmail: true
      }),
      EmailError
    );

  getSecurityPromptParameters = (): Promise<
    Result<GetSecurityPromptParametersReturnType, AccountSecurityError>
  > => toResult(httpService.get(GET_SECURITY_PROMPT_PARAMETERS_CONFIG), AccountSecurityError);
}

/**
 * An interface encapsulating the various HTTP requests in this web app.
 *
 * Note that all of the requests return `Result` types, which can be explicitly
 * instantiated with either a response value or an error, depending on whether
 * the request succeeded. The requests should only throw an error if a fatal
 * (i.e. unexpected) exception occurred.
 *
 * This interface type offers future flexibility e.g. for mocking the default
 * request service.
 */
export type RequestService = { [K in keyof RequestServiceDefault]: RequestServiceDefault[K] };
